import React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import InputLabelWrapper from "components/basic/input-label-wrapper/InputLabelWrapper";
import { MdmObjectDto, MdmObjectJobDtoInput, SeverityType, StatusType, useCreateJobMutation, useUpdateJobMutation } from "gql/generated/types";
import { GET_JOBS_BY_OBJECT_ID, GET_JOBS_BY_PROJECT_ID } from "gql/job.operations";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { T } from "services/lang/LanguageService";
import { z } from "zod";
import { priorityType, statusType } from "../utils/const";
import { errorToaster, getFormErrorMessage, successToaster } from "../utils/utils";

const schema = z.object({
  status: z.string().min(1, T("word.required-fields")),
  severity: z.string().min(1, T("word.required-fields")),
  description: z.string().min(1, T("word.required-fields")),
  title: z.string().min(1, T("word.required-fields")),
  startDate: z.string(),
  endDate: z.string(),
  objectId: z.string().optional(),
  id: z.number().optional(),
})

type JobFormProps = {
  object: MdmObjectDto;
  closeDialog: () => void;
  job: MdmObjectJobDtoInput | null
};


const JobForm: React.FC<JobFormProps> = ({ object, closeDialog, job }) => {
  const {projectId} = useParams();
  const defaultValues: MdmObjectJobDtoInput = {
    status: StatusType.Todo,
    severity: SeverityType.Medium,
    description: "",
    title: "",
    startDate: new Date().toISOString(),
    endDate: new Date().toISOString(),
  };

  const {control, handleSubmit, formState: { errors }, reset} = useForm<MdmObjectJobDtoInput>({
    defaultValues: job ?? defaultValues,
    resolver: zodResolver(schema),
  });

  const [createJobMutation] = useCreateJobMutation({
    onCompleted: () => successToaster(T("alert.job-created-successfully")),
    onError: errorToaster,
  });
  
  const [updateJobMutation] = useUpdateJobMutation({
    onCompleted: () => successToaster(T("alert.job-updated-successfully")),
    onError: errorToaster,
  });


  const handleCreateJob: SubmitHandler<MdmObjectJobDtoInput> = formData => {
    createJobMutation({
      variables: {
        objectId: object.id!,
        input: formData,
      },
      update: (cache, { data: qData }) => {
        cache.updateQuery({ query: GET_JOBS_BY_OBJECT_ID, variables: {id: object.id}}, (oldData) => ({ 
          allJobsByObjectId: [...oldData.allJobsByObjectId, qData?.createJob]
        }) );
        cache.updateQuery({ query: GET_JOBS_BY_PROJECT_ID, variables: {id: projectId}}, (oldData) => oldData ? ({ 
          allJobsByProjectId: [...oldData.allJobsByProjectId, qData?.createJob]
        }) : null);
        cache.modify({
          id: cache.identify({ id: object?.id, __typename: "MdmObjectDTO" }),
          fields: {
            info: info => {
              return {
                ...info,
                jobsCount: info.jobsCount + 1,
              };
            },
          },
        });
      }
    });
    reset();
    closeDialog();
  };

  const handleUpdateJob: SubmitHandler<MdmObjectJobDtoInput> = formData => {
    updateJobMutation({
      variables: {
        input: formData,
      },
    });
    reset();
    closeDialog();
  };

  return (
    <form className="flex flex-wrap">
      <Controller
        name="status"
        control={control}
        render={({ field }) => (
          <InputLabelWrapper labelName={T("word.status")} col={4}>
            <Dropdown
              value={field.value}
              options={Object.keys(statusType)}
              className="p-inputtext-sm"
              placeholder={T("word.select-one")}
              onChange={e => field.onChange(e.target.value)}
            />
            {getFormErrorMessage(field.name, errors)}
          </InputLabelWrapper>
        )}
      />
      <Controller
        name="severity"
        control={control}
        render={({ field }) => (
          <InputLabelWrapper labelName={T("word.priority")} col={4}>
            <Dropdown
              value={field.value}
              options={Object.keys(priorityType)}
              placeholder={T("word.select-one")}
              className="p-inputtext-sm"
              onChange={e => field.onChange(e.target.value)}
            />
            {getFormErrorMessage(field.name, errors)}
          </InputLabelWrapper>
        )}
      />
      <Controller
        name="title"
        control={control}
        render={({ field }) => (
          <InputLabelWrapper labelName={T("word.title")} col={4}>
            <InputText
              value={field.value ?? ""}
              className="p-inputtext-sm"
              onChange={e => field.onChange(e.target.value)}
            />
            {getFormErrorMessage(field.name, errors)}
          </InputLabelWrapper>
        )}
      />
      <Controller
        name="startDate"
        control={control}
        render={({ field }) => (
          <InputLabelWrapper labelName={T("word.start-date")} col={4}>
            <Calendar
              value={new Date(field.value)}
              dateFormat="yy-mm-dd"
              onChange={e => field.onChange((e.target.value as Date).toISOString())}
            />
          </InputLabelWrapper>
        )}
      />
      <Controller
        name="endDate"
        control={control}
        render={({ field }) => (
          <InputLabelWrapper labelName={T("word.end-date")}>
            <Calendar
              value={new Date(field.value)}
              dateFormat="yy-mm-dd"
              onChange={e => field.onChange((e.target.value as Date).toISOString())}
            />
            {getFormErrorMessage(field.name, errors)}
          </InputLabelWrapper>
        )}
      />
      <Controller
        name="description"
        control={control}
        render={({ field }) => (
          <InputLabelWrapper labelName={T("word.description")} className="w-full" col={12}>
            <InputTextarea
              value={field.value ?? ""}
              rows={4}
              maxLength={255}
              className="p-inputtext-sm"
              onChange={e => field.onChange(e.target.value)}
            />
            {getFormErrorMessage(field.name, errors)}
          </InputLabelWrapper>
        )}
      />
      {job ? (
        <Button
          label={T("action.update")}
          className="mt-3 ml-2 p-button-success"
          onClick={handleSubmit(handleUpdateJob)}
        />
      ) : (
        <Button
          label={T("action.create")}
          className="mt-3 ml-2 p-button-success"
          onClick={handleSubmit(handleCreateJob)}
        />
      )}
    </form>
  );
}

export default JobForm;